/// <reference types="./types/broker-globals" />
import { lazy, Suspense, useEffect } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminRoute } from "@/components/auth/AdminRoute";
import { BrandAdminRoute } from "@/components/auth/BrandAdminRoute";
import { ModuleRoute } from "@/components/auth/ModuleRoute";
import { FeatureRoute } from "@/components/auth/FeatureRoute";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { BrandProvider } from "@/hooks/useBrand";
import { DomainGate } from "@/components/auth/DomainGate";
import { PageLoader } from "@/components/layout/PageLoader";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";
import { isChunkLoadError, shouldAutoReload } from "@/lib/chunk-reload";
import { toast } from "sonner";
import { reportError } from '@/lib/error-tracking';
import { isMarketingDomain } from '@/lib/domain';
import { Analytics } from '@vercel/analytics/react';
import { MetaPixelProvider } from '@/components/tracking/MetaPixelProvider';

import { SoftphoneContainer } from '@/components/softphone/SoftphoneContainer';

// Lazy-loaded page components
const Home = lazy(() => import("./pages/Home"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const BuyerIntake = lazy(() => import("./pages/BuyerIntake"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Accounts = lazy(() => import("./pages/Accounts"));
const AccountDetail = lazy(() => import("./pages/AccountDetail"));
const Matches = lazy(() => import("./pages/Matches"));

const Listings = lazy(() => import("./pages/Listings"));
const ListingsTriage = lazy(() => import("./pages/ListingsTriage"));
const ListingDetailV2 = lazy(() => import("./pages/ListingDetailV2"));
const PublicListing = lazy(() => import("./pages/PublicListing"));
const Leads = lazy(() => import("./pages/Leads"));
const LeadDetail = lazy(() => import("./pages/LeadDetail"));
const AiInbox = lazy(() => import("./pages/AiInbox"));
const ColdReplies = lazy(() => import("./pages/ColdReplies"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const CampaignDetail = lazy(() => import("./pages/CampaignDetail"));
const Inquiries = lazy(() => import("./pages/Inquiries"));
const AdminTeam = lazy(() => import("./pages/admin/Team"));
const AdminIntegrations = lazy(() => import("./pages/admin/Integrations"));
const AdminBuyerCriteriaRequests = lazy(() => import("./pages/admin/BuyerCriteriaRequests"));
const AdminBuyerVerifications = lazy(() => import("./pages/admin/BuyerVerifications"));
const AdminSubscriptions = lazy(() => import("./pages/admin/Subscriptions"));
const AdminBuyerNotifications = lazy(() => import("./pages/admin/BuyerNotifications"));
const ListingsPipeline = lazy(() => import("./pages/admin/listings/Pipeline"));
const ListingsTaxonomy = lazy(() => import("./pages/admin/listings/Taxonomy"));
const ListingsLocations = lazy(() => import("./pages/admin/listings/Locations"));
const AdminEnrichment = lazy(() => import("./pages/admin/Enrichment"));
const AdminZapmail = lazy(() => import("./pages/admin/Zapmail"));
const AdminAnalytics = lazy(() => import("./pages/admin/Analytics"));
const AdminKnowledgeBank = lazy(() => import("./pages/admin/KnowledgeBank"));
const AdminSystemHealth = lazy(() => import("./pages/admin/SystemHealth"));
const AdminEmailOps = lazy(() => import("./pages/admin/EmailOps"));
const AdminUtmBuilder = lazy(() => import("./pages/admin/UtmBuilder"));
const AdminUtmAttribution = lazy(() => import("./pages/admin/UtmAttribution"));
const AdminBrands = lazy(() => import("./pages/admin/Brands"));
const BrandSettings = lazy(() => import("./pages/settings/BrandSettings"));
const BrandTeam = lazy(() => import("./pages/settings/BrandTeam"));
const Blog = lazy(() => import("./pages/Blog"));
const Contact = lazy(() => import("./pages/Contact"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const AdminContent = lazy(() => import("./pages/admin/Content"));
const Roadmap = lazy(() => import("./pages/Roadmap"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Settings = lazy(() => import("./pages/Settings"));
const SellerOpps = lazy(() => import("./pages/SellerOpps"));
const SellerOppDetail = lazy(() => import("./pages/SellerOppDetail"));
const SocialPosts = lazy(() => import("./pages/SocialPosts"));
const Studio = lazy(() => import("./pages/Studio"));
const ContactsModule = lazy(() => import("./pages/ContactsModule"));
const SellerIntake = lazy(() => import("./pages/SellerIntake"));
const PublicSellerIntakeStart = lazy(() => import("./pages/PublicSellerIntakeStart"));
const SellerValueMagnet = lazy(() => import("./pages/SellerValueMagnet"));
const BuyerDataRoom = lazy(() => import("./pages/BuyerDataRoom"));
const BuyerPreferences = lazy(() => import("./pages/BuyerPreferences"));
const NDASigningPage = lazy(() => import("./pages/NDASigningPage"));
const Conversations = lazy(() => import("./pages/Conversations"));
const MessagingAnalytics = lazy(() => import("./pages/admin/MessagingAnalytics"));
const CalendarPage = lazy(() => import("./pages/Calendar"));
const PhonePage = lazy(() => import("./pages/Phone"));
const AdminHome = lazy(() => import("./pages/admin/AdminHome"));
const Contracts = lazy(() => import("./pages/Contracts"));
const ContractEditor = lazy(() => import("./pages/ContractEditor"));
const EmailTemplates = lazy(() => import("./pages/EmailTemplates"));
// EmailTemplateEditor route removed in #989 (Phase F). The editor file stays
// compilable until PR2 deletes it alongside the brand_notification_templates
// table drop. Keeping the lazy import out keeps it out of the bundle.
const PaidMedia = lazy(() => import("./pages/PaidMedia"));
const MatchPassed = lazy(() => import("./pages/MatchPassed"));
// Buyer onboarding flow (#2134) — magic-link landing pad + wizard + reveal.
// Renders the #2135 holding stub itself while app_settings.
// buyer_onboarding_enabled is off (the default).
const BuyerOnboarding = lazy(() => import("./pages/BuyerOnboarding"));
const MyFrankly = lazy(() => import("./pages/MyFrankly"));
const BuyerSubscription = lazy(() => import("./pages/BuyerSubscription"));
const MatchExpired = lazy(() => import("./pages/MatchExpired"));
const MatchUnavailable = lazy(() => import("./pages/MatchUnavailable"));
const NDAVerify = lazy(() => import("./pages/NDAVerify"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Global safety net: catch chunk-load failures that escape React Suspense
// (e.g., a fire-and-forget dynamic import in a hook effect). Shares the
// TTL+counter state in src/lib/chunk-reload.ts with the ErrorBoundary so
// the two handlers cooperate on a single attempt budget instead of
// stomping on each other's sessionStorage flag (which used to produce
// the infinite "A new version is available" loop — follow-up to #1313).
function useChunkErrorListener() {
  useEffect(() => {
    const handler = (e: PromiseRejectionEvent) => {
      if (!isChunkLoadError(e.reason)) return;
      e.preventDefault();
      // shouldAutoReload bumps the shared counter and tells us whether
      // we're still under the cap. If we're at the cap, do nothing —
      // the next React render that touches a chunk will hit the
      // ErrorBoundary, which surfaces the persistent UI.
      if (shouldAutoReload()) {
        window.location.reload();
      }
    };
    window.addEventListener('unhandledrejection', handler);
    return () => window.removeEventListener('unhandledrejection', handler);
  }, []);
}

// Redirect component for legacy /buyers/:id routes
function BuyerRedirect() {
  const { id } = useParams();
  return <Navigate to={`/accounts/${id}`} replace />;
}

function MatchRedirect() {
  const { id } = useParams();
  return <Navigate to={`/matches?match=${id}`} replace />;
}

// Redirect component for legacy /old-leads/:id routes
function LegacyLeadRedirect() {
  const { id } = useParams();
  return <Navigate to={`/leads/${id}`} replace />;
}

// Redirect legacy /listings/:id to the command-center layout (/listings/:id/v2 is the only layout now)
function ListingV2Redirect() {
  const { id } = useParams();
  return <Navigate to={`/listings/${id}/v2`} replace />;
}

// Redirect /prospects/:id to /leads/:id
function ProspectToLeadRedirect() {
  const { id } = useParams();
  return <Navigate to={`/leads/${id}`} replace />;
}

// App domain root: redirect to /auth or /dashboard based on auth state
function AppRoot() {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/auth" replace />;
  return <Navigate to="/dashboard" replace />;
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      reportError(error, { component: 'QueryCache', queryKey: String(query.queryKey) });
      // Only surface errors for queries that have already loaded data (background refetch failures).
      // Suppress transient network errors — they resolve on the next automatic refetch.
      const isNetworkError = error instanceof TypeError && /fetch|network/i.test(error.message);
      if (query.state.data !== undefined && !isNetworkError) {
        toast.error(`Something went wrong: ${error.message}`);
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      reportError(error, { component: 'MutationCache' });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
      networkMode: 'online', // Pause queries when browser is offline/resource-exhausted
    },
  },
});

// Strip the `?_cb=<ts>` cache-buster the ErrorBoundary's Refresh button
// appends. Without this, the param sticks in the URL forever and gets
// dragged along by every internal navigation that preserves query params
// (filter pages in /listings, /matches, /inbox, etc.).
function useStripCacheBusterParam() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (url.searchParams.has('_cb')) {
      url.searchParams.delete('_cb');
      window.history.replaceState(null, '', url.toString());
    }
  }, []);
}

function ChunkErrorGuard({ children }: { children: React.ReactNode }) {
  useChunkErrorListener();
  useStripCacheBusterParam();
  return <>{children}</>;
}

const App = () => (
  <HelmetProvider>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrandProvider>
          <DomainGate>
          <TooltipProvider>
            <Analytics />
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <MetaPixelProvider>
              <ChunkErrorGuard>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public routes */}
                  <Route path="*" element={
                    <ErrorBoundary fallbackTitle="Page Error">
                      <Routes>
                        {isMarketingDomain() ? (
                          <>
                            <Route path="/" element={<Home />} />
                            <Route path="/privacy" element={<Privacy />} />
                            <Route path="/terms" element={<Terms />} />
                            <Route path="/buy" element={<BuyerIntake />} />
                            <Route path="/deal/:id" element={<PublicListing />} />
                            <Route path="/blog" element={<Blog />} />
                            <Route path="/blog/:slug" element={<BlogPost />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/seller-intake/start" element={<PublicSellerIntakeStart />} />
                            <Route path="/seller-intake/value" element={<SellerValueMagnet />} />
                            <Route path="/seller-intake/:token" element={<SellerIntake />} />
                            <Route path="/dataroom/:token" element={<BuyerDataRoom />} />
                            <Route path="/buyer/preferences/:token" element={<BuyerPreferences />} />
                            {/* Buyer onboarding flow (#2134). Flag-gated inside the pages:
                                buyer_onboarding_enabled off → holding stub / 404. */}
                            <Route path="/buyer/onboarding" element={<BuyerOnboarding />} />
                            <Route path="/buyer/subscription" element={<BuyerSubscription />} />
                            <Route path="/my-frankly" element={<MyFrankly />} />
                            <Route path="/my-frankly/join" element={<MyFrankly join />} />
                            <Route path="/match/passed" element={<MatchPassed />} />
                            <Route path="/match/expired" element={<MatchExpired />} />
                            <Route path="/match/unavailable" element={<MatchUnavailable />} />
                            <Route path="/nda/verify" element={<NDAVerify />} />
                            <Route path="/nda/:token" element={<NDASigningPage />} />
                          </>
                        ) : (
                          <>
                            <Route path="/" element={<AppRoot />} />
                            <Route path="/privacy" element={<Privacy />} />
                            <Route path="/terms" element={<Terms />} />
                            <Route path="/deal/:id" element={<PublicListing />} />
                            <Route path="/seller-intake/start" element={<PublicSellerIntakeStart />} />
                            <Route path="/seller-intake/value" element={<SellerValueMagnet />} />
                            <Route path="/seller-intake/:token" element={<SellerIntake />} />
                            <Route path="/dataroom/:token" element={<BuyerDataRoom />} />
                            <Route path="/buyer/preferences/:token" element={<BuyerPreferences />} />
                            {/* Buyer onboarding flow (#2134). Flag-gated inside the pages:
                                buyer_onboarding_enabled off → holding stub / 404. */}
                            <Route path="/buyer/onboarding" element={<BuyerOnboarding />} />
                            <Route path="/buyer/subscription" element={<BuyerSubscription />} />
                            <Route path="/my-frankly" element={<MyFrankly />} />
                            <Route path="/my-frankly/join" element={<MyFrankly join />} />
                            <Route path="/match/passed" element={<MatchPassed />} />
                            <Route path="/match/expired" element={<MatchExpired />} />
                            <Route path="/match/unavailable" element={<MatchUnavailable />} />
                            <Route path="/nda/verify" element={<NDAVerify />} />
                            <Route path="/nda/:token" element={<NDASigningPage />} />
                          </>
                        )}

                        {/* Auth */}
                        <Route path="/auth" element={<Auth />} />
                        <Route path="/reset-password" element={<ResetPassword />} />

                        {/* Legacy redirects */}
                        <Route path="/buyers" element={<Navigate to="/accounts" replace />} />
                        <Route path="/buyers/:id" element={<BuyerRedirect />} />
                        <Route path="/old-leads" element={<Navigate to="/leads" replace />} />
                        <Route path="/old-leads/:id" element={<LegacyLeadRedirect />} />
                        <Route path="/lead-lists" element={<Navigate to="/leads" replace />} />
                        <Route path="/lead-lists/:id" element={<Navigate to="/leads" replace />} />
                        <Route path="/jobs" element={<Navigate to="/leads?tab=jobs" replace />} />
                        <Route path="/bulk-actions" element={<Navigate to="/campaigns" replace />} />
                        <Route path="/prospects" element={<Navigate to="/leads" replace />} />
                        <Route path="/prospects/:id" element={<ProtectedRoute><ProspectToLeadRedirect /></ProtectedRoute>} />

                        {/* Dashboard */}
                        <Route path="/dashboard" element={<ProtectedRoute><ErrorBoundary fallbackTitle="Dashboard Error"><Dashboard /></ErrorBoundary></ProtectedRoute>} />

                        {/* Module routes */}
                        <Route path="/accounts" element={<ProtectedRoute><ModuleRoute module="accounts"><ErrorBoundary><Accounts /></ErrorBoundary></ModuleRoute></ProtectedRoute>} />
                        <Route path="/accounts/:id" element={<ProtectedRoute><ModuleRoute module="accounts"><ErrorBoundary><AccountDetail /></ErrorBoundary></ModuleRoute></ProtectedRoute>} />
                        <Route path="/matches" element={<ProtectedRoute><ModuleRoute module="matches"><FeatureRoute feature="buyer_matches"><ErrorBoundary><Matches /></ErrorBoundary></FeatureRoute></ModuleRoute></ProtectedRoute>} />
                        <Route path="/matches/:id" element={<MatchRedirect />} />
                        <Route path="/seller-opps" element={<ProtectedRoute><ModuleRoute module="seller_opps"><ErrorBoundary><SellerOpps /></ErrorBoundary></ModuleRoute></ProtectedRoute>} />
                        <Route path="/seller-opps/:id" element={<ProtectedRoute><ModuleRoute module="seller_opps"><ErrorBoundary><SellerOppDetail /></ErrorBoundary></ModuleRoute></ProtectedRoute>} />
                        <Route path="/listings" element={<ProtectedRoute><ModuleRoute module="listings"><ErrorBoundary><Listings /></ErrorBoundary></ModuleRoute></ProtectedRoute>} />
                        <Route path="/listings/triage" element={<ProtectedRoute><ModuleRoute module="listings"><ErrorBoundary><ListingsTriage /></ErrorBoundary></ModuleRoute></ProtectedRoute>} />
                        <Route path="/listings/:id" element={<ProtectedRoute><ModuleRoute module="listings"><ListingV2Redirect /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/listings/:id/v2" element={<ProtectedRoute><ModuleRoute module="listings"><ErrorBoundary><ListingDetailV2 /></ErrorBoundary></ModuleRoute></ProtectedRoute>} />
                        <Route path="/leads" element={<ProtectedRoute><ModuleRoute module="leads"><ErrorBoundary><Leads /></ErrorBoundary></ModuleRoute></ProtectedRoute>} />
                        <Route path="/leads/:id" element={<ProtectedRoute><ModuleRoute module="leads"><ErrorBoundary><LeadDetail /></ErrorBoundary></ModuleRoute></ProtectedRoute>} />
                        <Route path="/campaigns" element={<ProtectedRoute><ModuleRoute module="campaigns"><FeatureRoute feature="campaigns"><ErrorBoundary><Campaigns /></ErrorBoundary></FeatureRoute></ModuleRoute></ProtectedRoute>} />
                        <Route path="/campaigns/:id" element={<ProtectedRoute><ModuleRoute module="campaigns"><FeatureRoute feature="campaigns"><ErrorBoundary><CampaignDetail /></ErrorBoundary></FeatureRoute></ModuleRoute></ProtectedRoute>} />
                        <Route path="/inbox" element={<ProtectedRoute><ModuleRoute module="inbox"><ErrorBoundary><AiInbox /></ErrorBoundary></ModuleRoute></ProtectedRoute>} />
                        <Route path="/cold-replies" element={<ProtectedRoute><ModuleRoute module="inbox"><FeatureRoute feature="cold_replies"><ErrorBoundary><ColdReplies /></ErrorBoundary></FeatureRoute></ModuleRoute></ProtectedRoute>} />
                        <Route path="/conversations" element={<ProtectedRoute><ModuleRoute module="unified_inbox"><ErrorBoundary><Conversations /></ErrorBoundary></ModuleRoute></ProtectedRoute>} />
                        <Route path="/messages" element={<Navigate to="/conversations" replace />} />
                        <Route path="/unified-inbox" element={<Navigate to="/conversations" replace />} />
                        <Route path="/calendar" element={<ProtectedRoute><ModuleRoute module="calendar"><ErrorBoundary><CalendarPage /></ErrorBoundary></ModuleRoute></ProtectedRoute>} />
                        <Route path="/phone" element={<ProtectedRoute><ModuleRoute module="phone"><ErrorBoundary><PhonePage /></ErrorBoundary></ModuleRoute></ProtectedRoute>} />
                        {/* Call history folded into the Phone page's Calls tab (#2431) */}
                        <Route path="/phone/calls" element={<Navigate to="/phone" replace />} />
                        <Route path="/inquiries" element={<ProtectedRoute><ModuleRoute module="inquiries"><ErrorBoundary><Inquiries /></ErrorBoundary></ModuleRoute></ProtectedRoute>} />
                        <Route path="/contacts" element={<ProtectedRoute><ModuleRoute module="contacts"><ErrorBoundary><ContactsModule /></ErrorBoundary></ModuleRoute></ProtectedRoute>} />
                        <Route path="/contracts" element={<ProtectedRoute><ModuleRoute module="contracts"><ErrorBoundary><Contracts /></ErrorBoundary></ModuleRoute></ProtectedRoute>} />
                        <Route path="/contracts/new" element={<ProtectedRoute><ModuleRoute module="contracts"><ErrorBoundary><ContractEditor /></ErrorBoundary></ModuleRoute></ProtectedRoute>} />
                        <Route path="/contracts/:id/edit" element={<ProtectedRoute><ModuleRoute module="contracts"><ErrorBoundary><ContractEditor /></ErrorBoundary></ModuleRoute></ProtectedRoute>} />

                        {/* Admin routes */}
                        <Route path="/admin" element={<AdminRoute><ErrorBoundary><AdminHome /></ErrorBoundary></AdminRoute>} />
                        <Route path="/admin/team" element={<AdminRoute><ErrorBoundary><AdminTeam /></ErrorBoundary></AdminRoute>} />
                        <Route path="/admin/integrations" element={<AdminRoute><ErrorBoundary><AdminIntegrations /></ErrorBoundary></AdminRoute>} />
                        <Route path="/admin/buyer-criteria-requests" element={<AdminRoute><ErrorBoundary><AdminBuyerCriteriaRequests /></ErrorBoundary></AdminRoute>} />
                        <Route path="/admin/buyer-verifications" element={<AdminRoute><ErrorBoundary><AdminBuyerVerifications /></ErrorBoundary></AdminRoute>} />
                        <Route path="/admin/buyer-notifications" element={<AdminRoute><ErrorBoundary><AdminBuyerNotifications /></ErrorBoundary></AdminRoute>} />
                        <Route path="/admin/subscriptions" element={<AdminRoute><ErrorBoundary><AdminSubscriptions /></ErrorBoundary></AdminRoute>} />

                        {/* Listings hub */}
                        <Route path="/admin/listings/pipeline" element={<AdminRoute><ErrorBoundary><ListingsPipeline /></ErrorBoundary></AdminRoute>} />
                        <Route path="/admin/listings/taxonomy" element={<AdminRoute><ErrorBoundary><ListingsTaxonomy /></ErrorBoundary></AdminRoute>} />
                        <Route path="/admin/listings/locations" element={<AdminRoute><ErrorBoundary><ListingsLocations /></ErrorBoundary></AdminRoute>} />

                        {/* Enrichment hub */}
                        <Route path="/admin/enrichment" element={<AdminRoute><ErrorBoundary><AdminEnrichment /></ErrorBoundary></AdminRoute>} />

                        {/* Redirects from old routes */}
                        <Route path="/admin/data" element={<Navigate to="/admin/listings/pipeline" replace />} />
                        <Route path="/admin/categorization" element={<Navigate to="/admin/listings/pipeline?tab=categorize" replace />} />
                        <Route path="/admin/industries" element={<Navigate to="/admin/listings/taxonomy" replace />} />
                        <Route path="/admin/zapmail" element={<AdminRoute><ErrorBoundary><AdminZapmail /></ErrorBoundary></AdminRoute>} />
                        <Route path="/admin/analytics" element={<AdminRoute><ErrorBoundary><AdminAnalytics /></ErrorBoundary></AdminRoute>} />
                        <Route path="/admin/knowledge-bank" element={<AdminRoute><ErrorBoundary><AdminKnowledgeBank /></ErrorBoundary></AdminRoute>} />
                        <Route path="/admin/content" element={<AdminRoute><ErrorBoundary><AdminContent /></ErrorBoundary></AdminRoute>} />
                        <Route path="/admin/system-health" element={<AdminRoute><ErrorBoundary><AdminSystemHealth /></ErrorBoundary></AdminRoute>} />
                        <Route path="/admin/messaging-analytics" element={<AdminRoute><ErrorBoundary><MessagingAnalytics /></ErrorBoundary></AdminRoute>} />
                        <Route path="/admin/email-ops" element={<AdminRoute><ErrorBoundary><AdminEmailOps /></ErrorBoundary></AdminRoute>} />
                        <Route path="/admin/utm-builder" element={<AdminRoute><ErrorBoundary><AdminUtmBuilder /></ErrorBoundary></AdminRoute>} />
                        <Route path="/admin/utm-attribution" element={<AdminRoute><ErrorBoundary><AdminUtmAttribution /></ErrorBoundary></AdminRoute>} />
                        <Route path="/admin/brands" element={<AdminRoute><ErrorBoundary><AdminBrands /></ErrorBoundary></AdminRoute>} />
                        <Route path="/roadmap" element={<AdminRoute><ErrorBoundary><Roadmap /></ErrorBoundary></AdminRoute>} />

                        {/* Brand admin settings */}
                        <Route path="/brand-settings" element={<ProtectedRoute><BrandAdminRoute><ErrorBoundary><BrandSettings /></ErrorBoundary></BrandAdminRoute></ProtectedRoute>} />
                        <Route path="/brand-team" element={<ProtectedRoute><BrandAdminRoute><ErrorBoundary><BrandTeam /></ErrorBoundary></BrandAdminRoute></ProtectedRoute>} />
                        <Route path="/email-templates" element={<ProtectedRoute><BrandAdminRoute><ErrorBoundary><EmailTemplates /></ErrorBoundary></BrandAdminRoute></ProtectedRoute>} />
                        {/* /email-templates/:code/edit removed in #989 — preview-only flow */}

                        {/* Settings */}
                        <Route path="/settings" element={<ProtectedRoute><ErrorBoundary><Settings /></ErrorBoundary></ProtectedRoute>} />
                        <Route path="/paid-media" element={<ProtectedRoute><ModuleRoute module="campaigns"><ErrorBoundary><PaidMedia /></ErrorBoundary></ModuleRoute></ProtectedRoute>} />
                        <Route path="/social-posts" element={<ProtectedRoute><FeatureRoute feature="social_posts"><ErrorBoundary><SocialPosts /></ErrorBoundary></FeatureRoute></ProtectedRoute>} />
                        <Route path="/studio" element={<ProtectedRoute><ModuleRoute module="studio"><ErrorBoundary><Studio /></ErrorBoundary></ModuleRoute></ProtectedRoute>} />

                        {/* 404 */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </ErrorBoundary>
                  } />
                </Routes>
              </Suspense>
              </ChunkErrorGuard>
              <SoftphoneContainer />
              </MetaPixelProvider>
            </BrowserRouter>
          </TooltipProvider>
          </DomainGate>
          </BrandProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </HelmetProvider>
);

export default App;
